import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { getCurrentUser } from "./api/client";
import { User } from "./types";
import Login from "./components/Login";
import ChangePassword from "./components/ChangePassword";
import ControlAuthorityHome from "./components/ControlAuthorityHome";
import IUDashboard from "./components/Dashboard/IUDashboard";
import CoordinatorDashboard from "./components/Dashboard/CoordinatorDashboard";
import AdminDashboard from "./components/Dashboard/AdminDashboard";
import FinanceDashboard from "./components/Dashboard/FinanceDashboard";
import SampleDetail from "./components/Samples/SampleDetail";
import AssistantWidget from "./components/Assistant/AssistantWidget";
import ToastContainer from "./ui/ToastContainer";
import { DemoBanner } from "./components/Demo";

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then(r => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={styles.loading}>Loading…</div>;

  // Force a password change before anything else when an admin/CLI issued a temp password.
  if (user && user.must_change_password) {
    return (
      <>
        <ToastContainer />
        <ChangePassword forced onChanged={setUser} onLogout={() => setUser(null)} />
      </>
    );
  }

  const DashboardForRole = () => {
    if (!user) return <Navigate to="/login" />;
    switch (user.role) {
      case "iu":          return <IUDashboard user={user} onLogout={() => setUser(null)} />;
      case "coordinator": return <CoordinatorDashboard user={user} onLogout={() => setUser(null)} />;
      case "admin":       return <AdminDashboard user={user} onLogout={() => setUser(null)} />;
      case "finance":     return <FinanceDashboard user={user} onLogout={() => setUser(null)} />;
      default:            return <Navigate to="/login" />;
    }
  };

  return (
    <BrowserRouter>
      <DemoBanner />
      <ToastContainer />
      <Routes>
        <Route path="/login" element={
          user ? <Navigate to="/" /> : <Login onLogin={setUser} />
        } />
        <Route path="/" element={
          // An industrial user and the finance office go straight to their own
          // dashboard; the Control Authority home page is for program staff.
          !user ? <Navigate to="/login" />
          : user.role === "iu" || user.role === "finance" ? <Navigate to="/dashboard" />
          : <ControlAuthorityHome user={user} onLogout={() => setUser(null)} />
        } />
        <Route path="/dashboard/*" element={<DashboardForRole />} />
        <Route path="/samples/:id" element={
          user ? <SampleDetail /> : <Navigate to="/login" />
        } />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
      {user && <AssistantWidget />}
    </BrowserRouter>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: { display: "flex", alignItems: "center", justifyContent: "center",
             height: "100vh", fontSize: 18, color: "#4a5568" },
};
